import { Injectable, Provider } from '@angular/core';
import { PaginatedResponse } from '../../shared/pagination/paginated-response';
import { ApiClient, ApiRestrictedClient, CreateApiClientBody, UpdateApiClientBody } from '../api-types/client.api';
import { ApiCreateRelative, ApiRelative, ApiUpdateRelative } from '../api-types/relative.api';
import { Client, ClientName, CreateClientBody, RestrictedClient, UpdateClient } from '../app-types/client.app';
import { CreateRelative, Relative, UpdateRelative } from '../app-types/relative.app';
import { CLIENT_API } from '../injection-tokens/services.token';
import {
	transformAnyApiClientToAppClient,
	transformApiClientsToAppClients,
	transformApiClientToAppClient,
	transformCreateClientToCreateApiClientBody,
	transformUpdateClientToUpdateClientBody,
} from '../transformers/client.transform';
import {
	transformApiRelativeNeighborsToAppRelativeNeighbors,
	transformApiRelativesToAppRelatives,
	transformApiRelativeToAppRelative,
	transformCreateRelativeToApiCreateRelative,
	transformUpdateRelativeToApiUpdateRelative,
} from '../transformers/relative.transform';
import { BaseRequestService } from './base-request.service';
import { DashboardItem } from '../app-types/dashboard.app';
import { ApiDashboardItem } from '../api-types/dashboard.api';
import { transformApiDashboardItemsToAppDashboardItems } from '../transformers/dashboard.transform';
import { NeighborContext } from '../app-types/neighbors.app';
import { createRelative } from '../../mock-data/relative.mock';
import { ApiNeighborContext } from '../api-types/neighbor.api';

@Injectable()
export class ClientService extends BaseRequestService implements ClientApiProvider {
	public getAll = async (offset?: number, range?: number): Promise<PaginatedResponse<Client>> => {
		const response = await this.get<PaginatedResponse<ApiClient>>('clients', { offset, range });
		const clients = transformApiClientsToAppClients(response.entities);
		return {
			...response,
			entities: clients,
		};
	};

	public getAllDashboard = async (
		offset?: number,
		range?: number,
		userId?: string,
	): Promise<PaginatedResponse<DashboardItem>> => {
		const response = await this.get<PaginatedResponse<ApiDashboardItem>>('dashboard', {
			offset,
			range,
			userId,
		});
		const clients = transformApiDashboardItemsToAppDashboardItems(response.entities);
		return {
			...response,
			entities: clients,
		};
	};

	public getSingleWithRestricted = async (id: number) => {
		const apiClient = await this.get<ApiClient | ApiRestrictedClient>(`clients/${id}`);
		const response = transformAnyApiClientToAppClient(apiClient);
		return response;
	};

	public getSingle = async (id: number) => {
		const apiClient = await this.get<ApiClient>(`clients/${id}`);
		const response = transformApiClientToAppClient(apiClient);
		return response;
	};

	public create = async (data: CreateClientBody) => {
		const body = transformCreateClientToCreateApiClientBody(data);
		const response = await this.post<ApiClient, CreateApiClientBody>('clients', body);
		const result = transformApiClientToAppClient(response);
		return result;
	};

	public update = async (id: number, data: UpdateClient) => {
		const body = transformUpdateClientToUpdateClientBody(data);
		return await this.put<UpdateApiClientBody, UpdateApiClientBody>(`clients/${id}`, body);
	};

	public getRelatives = async (clientId: number | string, offset?: number, range?: number) => {
		const response = await this.get<PaginatedResponse<ApiRelative>>(`clients/${clientId}/environment`, {
			offset,
			range,
		});
		const users = transformApiRelativesToAppRelatives(response.entities);
		const result: PaginatedResponse<Relative> = {
			entities: users,
			totalEntities: response.totalEntities,
			offset: response.offset,
			range: response.range,
		};
		return result;
	};

	public getRelative = async (clientId: number, relativeId: number) => {
		const apiRelative = await this.get<ApiRelative>(`clients/${clientId}/environment/${relativeId}`);
		const relative = transformApiRelativeToAppRelative(apiRelative);
		return relative;
	};

	public createRelative = async (clientId: number, data: CreateRelative) => {
		const body = transformCreateRelativeToApiCreateRelative(data);
		const response = await this.post<{ id: number }, ApiCreateRelative>(`clients/${clientId}/environment`, body);
		return response.id;
	};

	public updateRelative = async (clientId: number, relativeId: number | string, data: UpdateRelative) => {
		const body = transformUpdateRelativeToApiUpdateRelative(data);
		const response = await this.put<{ id: number }, ApiUpdateRelative>(
			`clients/${clientId}/environment/${relativeId}`,
			body,
		);
		return response.id;
	};

	public deleteRelative = async (clientId: string | number, relativeId: number | string) => {
		await this.remove<void>(`clients/${clientId}/environment/${relativeId}`);
	};

	public delete = async (id: number) => {
		await this.remove<void>(`clients/${id}`);
	};

	public getCanClientBeDeleted = async (id: number) => {
		const result = await this.get<boolean>(`validation/canDeleteClient/${id}`);
		return result;
	};

	public getRelativeNeighbors = async (
		clientId: number | string,
		relativeId: number | string,
	): Promise<NeighborContext<Relative>> => {
		const response = await this.get<ApiNeighborContext<ApiRelative>>(
			`clients/${clientId}/environment/${relativeId}/neighbors`,
		);
		return transformApiRelativeNeighborsToAppRelativeNeighbors(response);
	};

	/**
	 * Get the name of a client by ID. Fetches client data but preferred from cache.
	 * Maps the result to a ClientName object.
	 * @param id ID of the client
	 * @returns Name of the client
	 */
	public getNameOnly = async (id: number) => {
		const apiClient = await this.get<ApiClient>(
			`clients/${id}`,
			{},
			{
				cache: 'force-cache',
			},
		);
		if (!apiClient) return;
		const client = transformApiClientToAppClient(apiClient);
		const response: ClientName = {
			firstName: client.firstName ?? '',
			name: client.name,
			displayName: `${client.firstName} ${client.name}`,
		};
		return response;
	};
}

export interface ClientApiProvider extends BaseRequestService {
	getAll(offset?: number, range?: number): Promise<PaginatedResponse<Client>>;

	getAllDashboard(offset?: number, range?: number, userId?: string): Promise<PaginatedResponse<DashboardItem>>;

	getSingle(id: number): Promise<Client>;

	create(data: CreateClientBody): Promise<Client>;

	update(id: number, data: UpdateClient): Promise<UpdateApiClientBody>;

	getRelatives(clientId: number | string, offset?: number, range?: number): Promise<PaginatedResponse<Relative>>;

	getRelative(clientId: number, relativeId: number): Promise<Relative>;

	createRelative(clientId: number, data: CreateRelative): Promise<number>;

	updateRelative(clientId: number, relativeId: number | string, data: UpdateRelative): Promise<number>;

	delete(id: number): Promise<void>;

	getCanClientBeDeleted(id: number): Promise<boolean>;

	getSingleWithRestricted(id: number): Promise<Client | RestrictedClient | undefined>;

	getNameOnly(id: number): Promise<ClientName | undefined>;

	getRelativeNeighbors(clientId: number | string, relativeId: number | string): Promise<NeighborContext<Relative>>;
}

@Injectable()
export class ClientMockApi extends BaseRequestService implements ClientApiProvider {
	public constructor() {
		super();
		console.warn('⛔️⛔️⛔️ Client Mock Service: You are currently using a mock service!');
	}

	public getAll = async (_offset?: number, _range?: number): Promise<PaginatedResponse<Client>> => {
		console.warn('[Mock] Getting all clients');
		return Promise.resolve({
			entities: [],
			totalEntities: 0,
			offset: 0,
			limit: 10,
			range: 10,
		});
	};

	public getAllDashboard(
		_offset?: number,
		_range?: number,
		_userId?: string,
	): Promise<PaginatedResponse<DashboardItem>> {
		console.warn('[Mock] Getting all dashboard clients');
		return Promise.resolve({
			entities: [],
			totalEntities: 0,
			offset: 0,
			range: 0,
		});
	}

	public getSingleWithRestricted = async (_id: number) => {
		console.warn('[Mock] Getting single client with restricted');
		return undefined;
	};

	public getSingle = async (_id: number) => {
		console.warn('[Mock] Getting single client');
		return {} as Client;
	};

	public create = async (_data: CreateClientBody) => {
		console.warn('[Mock] Creating client');
		return {} as Client;
	};

	public update = async (_id: number, _data: UpdateClient) => {
		console.warn('[Mock] Updating client');
		return {} as UpdateApiClientBody;
	};

	public getRelatives = async (_clientId: number | string, _offset?: number, _range?: number) => {
		console.warn('[Mock] Getting relatives');
		return Promise.resolve({
			entities: [],
			totalEntities: 0,
			offset: 0,
			range: 10,
		});
	};

	public getRelative = async (_clientId: number, _relativeId: number) => {
		console.warn('[Mock] Getting single relative');
		return {} as Relative;
	};

	public createRelative = async (_clientId: number, _data: CreateRelative) => {
		console.warn('[Mock] Creating relative');
		return Promise.resolve(1);
	};

	public updateRelative = async (_clientId: number, _relativeId: number | string, _data: UpdateRelative) => {
		console.warn('[Mock] Updating relative');
		return Promise.resolve(1);
	};

	public deleteRelative = async (_clientId: string | number, _relativeId: number | string) => {
		console.warn('[Mock] Deleting relative');
		return Promise.resolve();
	};

	public delete = async (_id: number) => {
		console.warn('[Mock] Deleting client');
		return Promise.resolve();
	};

	public getCanClientBeDeleted = async (_id: number) => {
		console.warn('[Mock] Checking if client can be deleted');
		return Promise.resolve(true);
	};

	public getNameOnly = async (_id: number) => {
		console.warn('[Mock] Getting client name only');
		return undefined;
	};

	public getRelativeNeighbors = async (
		_clientId: number | string,
		_relativeId: number | string,
	): Promise<NeighborContext<Relative>> => {
		console.warn('[Mock] Getting relative neighbors only');
		return Promise.resolve({
			current: createRelative({ id: '2' }),
			next: createRelative({ id: '3' }),
			previous: createRelative({ id: '1' }),
		});
	};
}

export const provideClientApi: Provider = {
	provide: CLIENT_API,
	useClass: ClientService,
};
